import { useMemo, useState } from "react";
import { Download, RefreshCw } from "lucide-react";
import { useApi } from "../hooks/useApi";
import advanceOutstandingApi from "../api/advanceOutstandingApi";
import usePerm from "../hooks/usePerm";
import { ADVANCE_TYPE_LABEL } from "../components/AdvanceOutstandingChips";
import Card from "../components/Card";
import Btn from "../components/Btn";
import PageHeader from "../components/PageHeader";
import SearchBar from "../components/SearchBar";
import StatusTabs from "../components/StatusTabs";
import { PageSpinner } from "../components/Spinner";
import T from "../theme/colors";

// What every employee and contractor still owes, bucket by bucket. The same
// figures the Job Work Entry and Fixed Salary deduction grids start from:
// money given minus money recovered, per bucket, never below 0.
const TYPES = ["ADVANCE", "LOAN", "MACHINERY", "PURCHASE", "PF", "ESI", "PT"];
const TABS = ["All", "Employees", "Contractors"];
const TAB_PARTY = { All: null, Employees: "EMPLOYEE", Contractors: "CONTRACTOR" };

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AdvanceOutstandingPage() {
  const { data, loading, refetch } = useApi(() => advanceOutstandingApi.list(), []);
  const can = usePerm("LEDGER_HUB");
  const [tab, setTab] = useState("All");
  const [search, setSearch] = useState("");

  const all = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const rows = useMemo(() => {
    const want = TAB_PARTY[tab];
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return all.filter(r => {
      if (want && r.partyType !== want) return false;
      if (!words.length) return true;
      const hay = [r.name, r.code, r.departmentName, r.partyType === "EMPLOYEE" ? "employee staff" : "contractor"]
        .filter(Boolean).join(" ").toLowerCase();
      return words.every(w => hay.includes(w));
    });
  }, [all, tab, search]);

  const counts = useMemo(() => ({
    All: all.length,
    Employees: all.filter(r => r.partyType === "EMPLOYEE").length,
    Contractors: all.filter(r => r.partyType === "CONTRACTOR").length,
  }), [all]);

  const totals = useMemo(() => {
    const t = { total: 0 };
    TYPES.forEach(k => { t[k] = 0; });
    rows.forEach(r => {
      TYPES.forEach(k => { t[k] += Number(r.buckets?.[k]) || 0; });
      t.total += Number(r.total) || 0;
    });
    return t;
  }, [rows]);

  const exportExcel = async () => {
    const XLSX = await import("xlsx");
    const head = ["Name", "Code", "Type", "Department", ...TYPES.map(k => ADVANCE_TYPE_LABEL[k]), "Total"];
    const body = rows.map(r => [
      r.name, r.code || "", r.partyType === "EMPLOYEE" ? "Employee" : "Contractor", r.departmentName || "",
      ...TYPES.map(k => Number(r.buckets?.[k]) || 0), Number(r.total) || 0,
    ]);
    const foot = ["Total", "", "", "", ...TYPES.map(k => Math.round(totals[k] * 100) / 100), Math.round(totals.total * 100) / 100];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([head, ...body, foot]), "Advance outstanding");
    XLSX.writeFile(wb, `advance-outstanding_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const th = {
    padding: "10px 12px", fontSize: 12, fontWeight: 700, color: T.text2, textTransform: "uppercase",
    textAlign: "right", borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap", background: T.bgHover,
  };
  const td = { padding: "9px 12px", fontSize: 14, textAlign: "right", whiteSpace: "nowrap" };

  return (
    <div>
      <PageHeader
        title="Advance Outstanding"
        subtitle="Advance, Loan / Baki, Machinery, Purchase, PF, ESI and PT still to be recovered — employees and contractors"
        actions={<div style={{ display: "flex", gap: 8 }}>
          <Btn variant="secondary" size="sm" icon={<RefreshCw size={13} />} onClick={refetch}>Refresh</Btn>
          {can.export && <Btn variant="primary" size="sm" icon={<Download size={13} />} onClick={exportExcel} disabled={!rows.length}>Excel</Btn>}
        </div>}
      />
      <StatusTabs tabs={TABS} active={tab} onSelect={setTab} counts={counts} />
      <Card style={{ marginTop: 12 }}>
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Search name, code, department…" />
        </div>
        {loading ? <PageSpinner /> : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: "left" }}>Name</th>
                  <th style={{ ...th, textAlign: "left" }}>Type</th>
                  {TYPES.map(k => <th key={k} style={th}>{ADVANCE_TYPE_LABEL[k]}</th>)}
                  <th style={th}>Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={TYPES.length + 3} style={{ ...td, textAlign: "center", color: T.text2, padding: 30 }}>
                    Nothing outstanding{search ? " for this search" : ""}.
                  </td></tr>
                ) : rows.map(r => (
                  <tr key={`${r.partyType}:${r.partyId}`} style={{ borderBottom: `1px solid ${T.border}` }}>
                    <td style={{ ...td, textAlign: "left" }}>
                      <div style={{ fontWeight: 700, color: T.text0 }}>{r.name}</div>
                      <div style={{ fontSize: 12, color: T.text2 }}>
                        {[r.code, r.departmentName].filter(Boolean).join(" · ") || "—"}
                      </div>
                    </td>
                    <td style={{ ...td, textAlign: "left" }}>
                      <span style={{
                        fontSize: 12, fontWeight: 700, padding: "2px 8px", borderRadius: 4,
                        background: r.partyType === "EMPLOYEE" ? "var(--primary-dim)" : "#F3E8FF",
                        color: r.partyType === "EMPLOYEE" ? "var(--primary)" : "#7C3AED",
                      }}>{r.partyType === "EMPLOYEE" ? "Employee" : "Contractor"}</span>
                    </td>
                    {TYPES.map(k => {
                      const v = Number(r.buckets?.[k]) || 0;
                      return <td key={k} style={{ ...td, color: v > 0 ? T.text0 : T.text2 }}>{v > 0 ? inr(v) : "—"}</td>;
                    })}
                    <td style={{ ...td, fontWeight: 800, color: "var(--danger)" }}>{inr(r.total)}</td>
                  </tr>
                ))}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr style={{ background: T.bgHover, fontWeight: 800 }}>
                    <td style={{ ...td, textAlign: "left" }} colSpan={2}>Total ({rows.length})</td>
                    {TYPES.map(k => <td key={k} style={td}>{totals[k] > 0 ? inr(totals[k]) : "—"}</td>)}
                    <td style={{ ...td, color: "var(--danger)" }}>{inr(totals.total)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
